import { all, call, put, select, takeEvery } from 'redux-saga/effects'
import { notificationsEnqueue } from 'src/features/notifications/NotificationsSlice'
import { actions as chatActions } from '../dougallChat/ChatSlice'
import { Fragment } from 'react'
import RoutedLink from '../../../../components/RoutedLink'
import { piwikSiteId } from '../../../../constants'
import { getRawHistory } from './HistorySelector'
import { actions as historyActions } from 'src/features/fw-ai/children/history/HistorySlice'
import { actions as liveFeedActions } from 'src/features/fw-ai/children/liveFeed/LiveFeedSlice'

const fragmentedNotification = (message, type, duration) => {
  return {
    message: (
      <Fragment>
        {message || 'Something went wrong!'} <RoutedLink label="Contact us" />
      </Fragment>
    ),
    duration
  }
}

/**
 * Request history
 * @param services
 * @param payload
 */
function* historyRequest(services, { payload = {} }) {
  const DougallService = services('DougallService')
  const SessionService = services('SessionService')

  const { loadLastThread } = payload

  try {
    // Get device
    // Call endpoint
    const deviceId = yield call(
      [SessionService, 'getCookie'],
      'V2_PSL_DEVICE_ID'
    )

    if (!deviceId) return
    const { history, raw: rawHistory } = yield call(
      [DougallService, 'historyRequest'],
      `${deviceId}`
    )
    yield put(historyActions.requestHistorySuccess({ history, rawHistory }))

    if (loadLastThread) {
      const lastThreadId = rawHistory[0]?.applicationLinkedPromptId
      yield call(promptHistoryRequest, services, {
        payload: { promptId: lastThreadId }
      })
      yield put(historyActions.setSelectedThread({ id: lastThreadId }))
    }
  } catch (e) {
    console.error(e)
    yield put(historyActions.requestHistoryFail())
  }
}

/**
 * Request prompt history
 * @param services
 * @param payload
 */
function* promptHistoryRequest(services, { payload }) {
  const DougallService = services('DougallService')
  const SessionService = services('SessionService')
  const StoriesRepository = services('StoriesRepository')
  const deviceId = yield call([SessionService, 'getCookie'], 'V2_PSL_DEVICE_ID')
  const siteId = parseInt(process.env.REACT_APP_PHNX_SITEID_V2)

  const { promptId = undefined, pId = undefined } = payload
  try {
    yield put(chatActions.resetMessages())
    yield put(chatActions.resetLinkedPromptId())
    yield put(chatActions.promptRequestStart())

    // Call endpoint
    const { messages, tags, lastAnswerTags } = yield call(
      [DougallService, 'promptHistoryRequest'],
      deviceId,
      promptId,
      pId,
      pId ? true : undefined
    )

    const { result } = yield call([DougallService, 'followUpPromptsRequest'], {
      applicationPromptId: messages[messages.length - 1]?.applicationPromptId,
      type: 'followupConversations',
      deviceId,
      siteId
    })

    const messagesWithRelatedReports = []
    let liveFeedTags = []
    for (let i = 0; i < messages.length; i++) {
      const message = messages[i]
      const { isAnswer, tags: messageTags, parserLLMAnswerTags } = message

      // Merge tags from both sources
      const parsedTags = [
        ...(messageTags || []),
        ...(parserLLMAnswerTags || [])
      ].filter(Boolean)

      if (isAnswer && parsedTags.length > 0) {
        const relatedReports = yield call(
          [StoriesRepository, 'getRelatedReportsByTags'],
          {
            tags: parsedTags.flatMap(tag => [tag.tagId, tag.tagName])
          }
        )

        if (i === messages.length - 1) {
          liveFeedTags = parsedTags.map(tag => tag.tagId)
        }

        const articles = relatedReports?.articles || []
        messagesWithRelatedReports.push({
          ...message,
          done: true,
          relatedReports: articles
        })
      } else {
        messagesWithRelatedReports.push({ ...message, done: true })
      }
    }

    if (liveFeedTags.length > 0) {
      yield put(liveFeedActions.requestLiveFeed(liveFeedTags))
    }

    yield put(chatActions.setLinkedPromptId(promptId))
    yield put(chatActions.addMessages(messagesWithRelatedReports))
    yield call([SessionService, 'saveToCache'], 'tags', lastAnswerTags)
    yield put(chatActions.addTags({ tags }))
    yield put(
      chatActions.editLastMessage({ followUpPrompts: result?.followUpPrompts })
    )

    yield put(chatActions.promptSuccess())
    yield put(chatActions.showInterests())
  } catch (e) {
    // Do not display error notification message for sharedPrompt flow
    if (!payload.pId || [400, 500].includes(e.getCode())) {
      yield put(
        notificationsEnqueue(
          fragmentedNotification(
            'There was a problem with the request.',
            'error'
          )
        )
      )
    }
    yield put(chatActions.promptFail())
  } finally {
    yield put(chatActions.setUrlRequested(true))
  }
}

/**
 * Request history
 * @param services
 * @param payload
 */
function* deleteHistoryPromptRequest(services, { payload }) {
  const DougallService = services('DougallService')
  const SessionService = services('SessionService')
  const DougallParser = services('DougallParser')
  const { id = undefined } = payload
  try {
    // Get device, we can assume the deviceId is ready for anon users since the history will load only when device id is ready
    const deviceId = yield call(
      [SessionService, 'getCookie'],
      'V2_PSL_DEVICE_ID'
    )

    const source = parseInt(piwikSiteId)

    // Call endpoint
    yield call([DougallService, 'deleteHistoryPrompt'], id, deviceId, source)

    yield put(historyActions.deleteHistorySuccess())
    yield put(historyActions.setClearAllOpen({ open: false }))
    // Update History list without
    let newHistory = [],
      rawHistory = []
    // If single id, remove entry from history list
    if (id) {
      rawHistory = yield select(getRawHistory())
      newHistory = rawHistory.filter(h => h.applicationPromptId !== id)
    }
    const parsedHistory = yield call([DougallParser, 'historyParser'], {
      history: newHistory
    })
    // Update history state
    yield put(
      historyActions.requestHistorySuccess({
        history: parsedHistory,
        rawHistory: newHistory
      })
    )
  } catch (e) {
    console.error(e)
    yield put(historyActions.deleteHistoryFail())
    yield put(notificationsEnqueue(fragmentedNotification()))
  }
}

/**
 * Request History Title Change
 * @param {*} services
 * @param {*} payload
 */
function* changeTitleRequest(services, { payload }) {
  const DougallService = services('DougallService')
  const SessionService = services('SessionService')
  const { id, title } = payload

  try {
    const deviceId = yield call(
      [SessionService, 'getCookie'],
      'V2_PSL_DEVICE_ID'
    )
    const source = parseInt(piwikSiteId)

    yield call([DougallService, 'titleChangeRequest'], {
      deviceId,
      applicationLinkedPromptId: id,
      title,
      source
    })
  } catch (e) {
    console.error(e)
  }
}

export default function* watchHistoryRequest(services) {
  yield all([
    takeEvery(historyActions.requestHistory, historyRequest, services)
  ])
  yield all([
    takeEvery(
      historyActions.deleteHistory,
      deleteHistoryPromptRequest,
      services
    )
  ])
  yield all([
    takeEvery(
      historyActions.requestPromptHistory,
      promptHistoryRequest,
      services
    )
  ])
  yield all([
    takeEvery(historyActions.requestTitleChange, changeTitleRequest, services)
  ])
}
